import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { MessageSquareWarning } from 'lucide-react';
import { auth, db, storage } from '../../firebase';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import { EcointelLottieMark } from '../../components/EcoIntelLoader';
import { Button } from '../../components/ui/Button';
import AdminTable from '../../components/admin/AdminTable';

const FEEDBACK_COLLECTION = 'feedback';

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
];

const KIND_LABELS = {
  bug: 'Bug',
  idea: 'Idea',
  question: 'Question',
  other: 'Other',
};

const SEVERITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const STATUS_PILL = {
  open: 'bg-slate-100 text-slate-700 border-slate-200',
  in_progress: 'bg-amber-50 text-amber-800 border-amber-200',
  complete: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    try { return value.toDate(); } catch (_) { return null; }
  }
  if (value instanceof Date) return value;
  const t = Date.parse(value);
  return Number.isFinite(t) ? new Date(t) : null;
}

function formatWhen(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function resolveStatus(data) {
  const raw = String(data?.status || 'open');
  return STATUSES.some((s) => s.value === raw) ? raw : 'open';
}

function statusLabel(value) {
  return STATUSES.find((s) => s.value === value)?.label || 'Open';
}

function isImageAttachment(att) {
  const type = String(att?.type || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  const name = String(att?.name || att?.path || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
}

function normalizeFeedback(id, data) {
  const created = toDate(data?.createdAt);
  return {
    id,
    ...data,
    status: resolveStatus(data),
    kind: data?.kind || 'other',
    severity: data?.severity || 'medium',
    summary: data?.summary || '',
    detail: data?.detail || '',
    expected: data?.expected || '',
    page: data?.page || '',
    email: data?.email || '',
    displayName: data?.displayName || '',
    uid: data?.uid || '',
    attachments: Array.isArray(data?.attachments) ? data.attachments : [],
    diagnostics: data?.diagnostics && typeof data.diagnostics === 'object' ? data.diagnostics : {},
    createdMs: created ? created.getTime() : 0,
  };
}

function Pill({ className, children }) {
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${className}`}>
      {children}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{label}</div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{children}</div>
    </div>
  );
}

export default function FeedbackAdmin() {
  const { formatError, logError } = useUserFacingError();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(['open', 'in_progress']);
  const [kindFilter, setKindFilter] = useState([]);
  const [severityFilter, setSeverityFilter] = useState([]);
  const [savingStatus, setSavingStatus] = useState(false);
  const [error, setError] = useState(null);
  const [attachmentUrls, setAttachmentUrls] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, FEEDBACK_COLLECTION),
      (snap) => {
        const out = snap.docs.map((d) => normalizeFeedback(d.id, d.data()));
        out.sort((a, b) => b.createdMs - a.createdMs);
        setRows(out);
        setLoading(false);
      },
      (err) => {
        logError('FeedbackAdmin listener failed', err);
        setError(formatError(err));
        setRows([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [formatError, logError]);

  useEffect(() => {
    if (selectedId && !rows.some((r) => r.id === selectedId)) setSelectedId(null);
  }, [rows, selectedId]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId],
  );

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!selected) {
        setAttachmentUrls({});
        return;
      }
      const next = {};
      await Promise.all((selected.attachments || []).map(async (att, idx) => {
        const key = `${selected.id}:${idx}`;
        if (att?.url) {
          next[key] = att.url;
          return;
        }
        if (!att?.path) return;
        try {
          next[key] = await getDownloadURL(ref(storage, att.path));
        } catch (err) {
          logError('FeedbackAdmin attachment URL failed', err);
        }
      }));
      if (!cancelled) setAttachmentUrls(next);
    };
    resolve();
    return () => { cancelled = true; };
  }, [selected, logError]);

  const statusCounts = useMemo(() => {
    const counts = { open: 0, in_progress: 0, complete: 0 };
    rows.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter.length && !statusFilter.includes(r.status)) return false;
      if (kindFilter.length && !kindFilter.includes(r.kind)) return false;
      if (severityFilter.length && !severityFilter.includes(r.severity)) return false;
      return true;
    });
  }, [rows, statusFilter, kindFilter, severityFilter]);

  const setStatus = async (item, status) => {
    if (!item || item.status === status) return;
    setSavingStatus(true);
    setError(null);
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db, FEEDBACK_COLLECTION, item.id), {
        status,
        statusUpdatedAt: serverTimestamp(),
        statusUpdatedBy: user?.uid || '',
        statusUpdatedByEmail: user?.email || '',
      });
    } catch (err) {
      logError('FeedbackAdmin status update failed', err);
      setError(formatError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const diag = selected?.diagnostics || {};

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
          <MessageSquareWarning size={22} /> Feedback
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          In-app reports from the account-menu Feedback form. Open a row to see the full
          report, then mark it in progress or complete.
        </p>
      </div>

      {error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      ) : null}

      <AdminTable
        rows={filteredRows}
        loading={loading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search summary, reporter, page…"
        searchPredicate={(r, q) => (
          (r.summary || '').toLowerCase().includes(q)
          || (r.detail || '').toLowerCase().includes(q)
          || (r.email || '').toLowerCase().includes(q)
          || (r.displayName || '').toLowerCase().includes(q)
          || (r.page || '').toLowerCase().includes(q)
          || (r.uid || '').toLowerCase().includes(q)
        )}
        filterGroups={[
          {
            id: 'status',
            label: 'Status',
            selected: statusFilter,
            onChange: setStatusFilter,
            options: STATUSES.map((s) => ({ value: s.value, label: s.label, count: statusCounts[s.value] || 0 })),
          },
          {
            id: 'kind',
            label: 'Kind',
            selected: kindFilter,
            onChange: setKindFilter,
            options: Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label })),
          },
          {
            id: 'severity',
            label: 'Severity',
            selected: severityFilter,
            onChange: setSeverityFilter,
            options: Object.entries(SEVERITY_LABELS).map(([value, label]) => ({ value, label })),
          },
        ]}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        defaultPageSize={25}
        emptyMessage="No in-app feedback reports yet. Pins left with the BugHerd sidebar stay on the BugHerd board."
        columns={[
          { key: 'status', label: 'Status', sortValue: (r) => r.status },
          { key: 'summary', label: 'Summary', sortValue: (r) => (r.summary || '').toLowerCase() },
          { key: 'kind', label: 'Kind', sortValue: (r) => r.kind },
          { key: 'severity', label: 'Severity', sortValue: (r) => ({ high: 3, medium: 2, low: 1 }[r.severity] || 0) },
          { key: 'reporter', label: 'Reporter', sortValue: (r) => (r.email || r.displayName || '').toLowerCase() },
          { key: 'createdAt', label: 'Filed', sortValue: (r) => r.createdMs },
        ]}
        renderRow={(r) => {
          const active = r.id === selectedId;
          return (
            <>
              <td className="p-3">
                <Pill className={STATUS_PILL[r.status] || STATUS_PILL.open}>{statusLabel(r.status)}</Pill>
              </td>
              <td className="p-3">
                <button
                  type="button"
                  onClick={() => setSelectedId(r.id)}
                  className={`text-left w-full ${active ? 'text-[#043132] font-semibold' : 'text-slate-800'}`}
                >
                  <div className="line-clamp-2 max-w-md">{r.summary || <span className="italic text-slate-400">No summary</span>}</div>
                  {r.page ? <div className="text-[10px] text-slate-400 mt-0.5 font-mono truncate max-w-md">{r.page}</div> : null}
                </button>
              </td>
              <td className="p-3">
                <Pill className="bg-slate-50 text-slate-700 border-slate-200">{KIND_LABELS[r.kind] || r.kind}</Pill>
              </td>
              <td className="p-3">
                <Pill className={r.severity === 'high'
                  ? 'bg-rose-50 text-rose-800 border-rose-200'
                  : r.severity === 'low'
                    ? 'bg-slate-50 text-slate-600 border-slate-200'
                    : 'bg-amber-50 text-amber-800 border-amber-200'}
                >
                  {SEVERITY_LABELS[r.severity] || r.severity}
                </Pill>
              </td>
              <td className="p-3">
                <div className="text-sm text-slate-800 truncate max-w-[180px]">
                  {r.anonymised ? 'Anonymised' : (r.displayName || r.email || r.uid || '—')}
                </div>
                {r.email && !r.anonymised ? (
                  <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{r.email}</div>
                ) : null}
              </td>
              <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{formatWhen(r.createdAt)}</td>
            </>
          );
        }}
      />

      {selected ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 space-y-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Pill className={STATUS_PILL[selected.status] || STATUS_PILL.open}>{statusLabel(selected.status)}</Pill>
                <Pill className="bg-slate-50 text-slate-700 border-slate-200">{KIND_LABELS[selected.kind] || selected.kind}</Pill>
                <Pill className={selected.severity === 'high'
                  ? 'bg-rose-50 text-rose-800 border-rose-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200'}
                >
                  {SEVERITY_LABELS[selected.severity] || selected.severity}
                </Pill>
                {selected.anonymised ? (
                  <Pill className="bg-slate-100 text-slate-600 border-slate-200">Reporter anonymised</Pill>
                ) : null}
              </div>
              <h2 className="text-lg font-bold text-slate-900">{selected.summary || 'Untitled report'}</h2>
              <p className="text-xs text-slate-500 mt-1">
                Filed {formatWhen(selected.createdAt)}
                {selected.page ? ` · ${selected.page}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {STATUSES.map((s) => {
                const active = selected.status === s.value;
                return (
                  <Button
                    key={s.value}
                    type="button"
                    variant={active ? 'primary' : 'secondary'}
                    className="text-sm px-3 py-1.5"
                    disabled={savingStatus || active}
                    onClick={() => setStatus(selected, s.value)}
                  >
                    {savingStatus && !active ? <EcointelLottieMark size={14} /> : null}
                    {s.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Reporter">
              {selected.anonymised
                ? 'Anonymised after account erasure'
                : (
                  <>
                    {selected.displayName || '—'}
                    {selected.email ? <div className="text-slate-500">{selected.email}</div> : null}
                    {selected.uid ? <div className="text-[11px] font-mono text-slate-400">{selected.uid}</div> : null}
                  </>
                )}
            </Field>
            <Field label="Page">
              {diag.url ? (
                <a href={diag.url} target="_blank" rel="noreferrer" className="text-[#043132] underline break-all">
                  {diag.url}
                </a>
              ) : (selected.page || '—')}
            </Field>
            <Field label="What happened">{selected.detail || '—'}</Field>
            <Field label="What they expected">{selected.expected || '—'}</Field>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Diagnostics</div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {[
                ['Browser', diag.browser],
                ['OS', diag.os],
                ['Viewport', diag.viewport],
                ['Screen', diag.screen],
                ['DPR', diag.dpr],
                ['Colour scheme', diag.colorScheme],
                ['Language', diag.language],
                ['Timezone', diag.timezone],
              ].map(([label, value]) => (
                <div key={label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                  <dt className="text-[10px] uppercase tracking-wider text-slate-400">{label}</dt>
                  <dd className="text-slate-800 mt-0.5 break-words">{value == null || value === '' ? '—' : String(value)}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">
              Attachments ({selected.attachments.length})
            </div>
            {selected.attachments.length === 0 ? (
              <p className="text-sm text-slate-500">No screenshots or files attached.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {selected.attachments.map((att, idx) => {
                  const url = attachmentUrls[`${selected.id}:${idx}`] || att.url || '';
                  const label = att.name || `Attachment ${idx + 1}`;
                  return (
                    <div key={`${selected.id}-${idx}`} className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                      {url && isImageAttachment(att) ? (
                        <a href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={label} className="w-full h-40 object-cover bg-white" />
                        </a>
                      ) : (
                        <div className="h-24 flex items-center justify-center text-xs text-slate-400 px-3 text-center">
                          {url ? 'File attached' : (att.error || 'Preview unavailable')}
                        </div>
                      )}
                      <div className="px-3 py-2 text-[11px] text-slate-600">
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer" className="text-[#043132] underline break-all">{label}</a>
                        ) : label}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selected.statusUpdatedAt || selected.statusUpdatedByEmail ? (
            <p className="text-[11px] text-slate-400">
              Status last updated {formatWhen(selected.statusUpdatedAt)}
              {selected.statusUpdatedByEmail ? ` by ${selected.statusUpdatedByEmail}` : ''}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-slate-500">Select a report in the table to see every field and update its status.</p>
      )}
    </div>
  );
}
